import { G2TrustBadge } from "@/components/TrustBadge";
import { TrustBadge } from "@/components/TrustBadge/TrustBadge";
import { FC } from "react";

type TrustBadgeBlockVendor = "g2" | "capterra" | "getapp" | "softwareadvice";

type TrustBadgeBlockProps = {
  vendor: TrustBadgeBlockVendor;
  reviewUrl?: string;
  badgeUrl?: string;
  alt?: string;
  title?: string;
  width: number;
  height: number;
};

const VENDOR_LABEL: Record<TrustBadgeBlockVendor, string> = {
  g2: "G2",
  capterra: "Capterra",
  getapp: "GetApp",
  softwareadvice: "Software Advice",
};

/**
 * Rewrites an absolute vendor image URL to our caching image proxy path
 * (see app/api/badge/[...path]/route.ts). Returns null for non-https URLs.
 */
const toProxyPath = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") {
      return null;
    }
    return `/api/badge/${parsed.host}${parsed.pathname}`;
  } catch {
    return null;
  }
};

/**
 * Renders a third-party review/award badge.
 *
 * - Static badges (Capterra, GetApp, Software Advice) are re-served through our
 *   caching proxy, giving them controlled cache headers and a warm same-origin
 *   connection with no manual asset upkeep.
 * - The G2 badge stays live (loaded directly from g2.com to respect their
 *   impression-tracked, `max-age=0` widget); its `preconnect`/`dns-prefetch`
 *   hints are hoisted into <head> by React so the connection warms early, and
 *   only on the pages where a G2 badge actually renders.
 */
export const TrustBadgeBlock: FC<TrustBadgeBlockProps> = ({
  vendor,
  reviewUrl,
  badgeUrl,
  alt,
  title,
  width,
  height,
}) => {
  if (!badgeUrl || !reviewUrl) {
    return null;
  }

  if (vendor === "g2") {
    return (
      <G2TrustBadge
        reviewUrl={reviewUrl}
        badgeUrl={badgeUrl}
        alt={alt}
        title={title}
        width={width}
        height={height}
      />
    );
  }

  const src = toProxyPath(badgeUrl);

  if (!src) {
    return null;
  }

  const accessibleAlt = alt || `Read reviews on ${VENDOR_LABEL[vendor]}`;

  return (
    <TrustBadge
      reviewUrl={reviewUrl}
      src={src}
      alt={accessibleAlt}
      title={title}
      width={width}
      height={height}
    />
  );
};
